import React, { useState, useCallback, useMemo } from 'react';
import propTypes from 'prop-types';

// constants
import { applicationTypes, applicationProperties } from 'constants/scripts';
import { NITROGEN_APPLICATION_RATE_TYPES } from 'constants/values';

// components
import BaseModal from 'common/modals/BaseModal';
import ZoneProductApplicationInputAppMode from 'edit/ZoneProductApplicationInputAppMode';
import ZoneProductApplicationInputWeb from 'edit/ZoneProductApplicationInputWeb';

// util
import { formatInputText } from 'util/formatters/textInputFormatter';
import { getMessage } from 'util/locale';
import { transformValidationMessage } from 'validations/utils';

const baseString = 'vra-script-product-application-modal';

function ScriptFertilizerApplicationModal({
  onSave,
  onCancel,
  isAppMode,
  isEditingDisabled,
  onChangeProductApplication,
  nitrogenRate,
  fertilizerRate,
  validateRate,
  transformNitrogenRateToFertilizer,
  transformFertilizerRateToNitrogen,
  nitrogenApplicationRateType
}) {
  const nitrogenProps = {
    title: getMessage('text__fertilizer-application-modal--nitrogen-amount'),
    transformTo: transformFertilizerRateToNitrogen,
    rate: nitrogenRate,
    type: NITROGEN_APPLICATION_RATE_TYPES.nitrogen,
    properties: applicationProperties.NITROGEN_RATE
  };
  const fertilizerProps = {
    title: getMessage('text__fertilizer-application-modal--fertilizer-amount'),
    transformTo: transformNitrogenRateToFertilizer,
    rate: fertilizerRate,
    type: NITROGEN_APPLICATION_RATE_TYPES.fertilizer,
    properties: applicationProperties.FERTILIZER_RATE
  };

  const [ editableField, convertedField ] = nitrogenApplicationRateType === NITROGEN_APPLICATION_RATE_TYPES.nitrogen
    ? [ nitrogenProps, fertilizerProps ]
    : [ fertilizerProps, nitrogenProps ];

  const initialValue = editableField.rate.scalar;

  const [newValue, setNewValue] = useState(initialValue);
  const [showKeypad, setShowKeypad] = useState(false); // only for APP_MODE

  const ZoneProductApplicationInput = isAppMode
    ? ZoneProductApplicationInputAppMode
    : ZoneProductApplicationInputWeb;

  const saveFields = useCallback(() => {
    let transformedRate = newValue;

    // If editing fertilizer rate on a nitrogen script, we need to convert that to nitrogen before saving
    if (editableField.type === NITROGEN_APPLICATION_RATE_TYPES.fertilizer) {
      transformedRate = transformFertilizerRateToNitrogen(transformedRate);
    }

    onChangeProductApplication({
      value: transformedRate,
      property: applicationProperties.NITROGEN_RATE
    });
    onSave();
  }, [newValue, onSave, onChangeProductApplication]);

  const validationResult = useMemo(() => {
    // If we're changing the nitrogen Rate, validate like always:
    if (editableField.properties === applicationProperties.NITROGEN_RATE) {
      return validateRate(newValue);
    }
    // If we're changing the fertilizer rate,
    // we need to convert the validation message so the correct max. amount is shown
    return transformValidationMessage(
      validateRate(transformFertilizerRateToNitrogen(newValue)),
      /(\d)\w+/,
      rate => Math.round(transformNitrogenRateToFertilizer(Number(rate))
      )
    );
  }, [newValue, validateRate, transformFertilizerRateToNitrogen,transformNitrogenRateToFertilizer, editableField]);

  return (
    <BaseModal className={baseString} onCancel={onCancel} onSave={saveFields}>
      <div className={`${baseString}--container`} data-test-id={`fertilizer-application-modal--${editableField.type}`}>
        <h3>{getMessage('title-text__fertilizer-application-modal')}</h3>
        <p>{getMessage('text__fertilizer-application-modal--description')}</p>
        <table>
          <tbody>
            <tr>
              <td>
                {editableField.title}
              </td>
              <td className="input-field">
                <div>
                  <ZoneProductApplicationInput
                    applicationType={applicationTypes.FERTILIZER}
                    isEditingDisabled={isEditingDisabled}
                    onChange={({value}) => setNewValue(value)}
                    onClick={() => setShowKeypad(true)}
                    onClose={() => setShowKeypad(false)}
                    popoverClassName={`${baseString}--keypad`}
                    property={editableField.properties}
                    showKeypad={showKeypad}
                    validationResult={validationResult}
                    value={formatInputText(newValue)}
                  />
                  <span className="unit">{editableField.rate.unitKey}</span>
                </div>
              </td>
            </tr>
            <tr>
              <td>
                {convertedField.title}
              </td>
              <td className="value-field">
                <span className="value">{formatInputText(convertedField.transformTo(newValue))}</span>
                <span className="unit">{convertedField.rate.unitKey}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </BaseModal>
  );
}

ScriptFertilizerApplicationModal.propTypes = {
  fertilizerRate: propTypes.object.isRequired,
  isAppMode: propTypes.bool,
  isEditingDisabled: propTypes.bool,
  nitrogenApplicationRateType: propTypes.string,
  nitrogenRate: propTypes.object.isRequired,
  onCancel: propTypes.func.isRequired,
  onChangeProductApplication: propTypes.func.isRequired,
  onSave: propTypes.func.isRequired,
  transformFertilizerRateToNitrogen: propTypes.func.isRequired,
  transformNitrogenRateToFertilizer: propTypes.func.isRequired,
  validateRate: propTypes.func.isRequired
};

export default ScriptFertilizerApplicationModal;
